/**
 * Helpers de vencimento para CNH, CRLV, seguro e outros documentos com data.
 * Comparações são feitas em dias inteiros no fuso local — datas de documento
 * são tratadas como `Date` sem componente de hora.
 */

export type ExpiryStatus = 'expired' | 'critical' | 'warning' | 'ok' | 'unknown'

export interface ExpiryInfo {
  status: ExpiryStatus
  /** Dias restantes (negativo = já venceu). `null` se a entrada for inválida ou ausente. */
  daysLeft: number | null
}

/** ≤ este número de dias dispara o status "critical" (vermelho). */
const CRITICAL_THRESHOLD_DAYS = 7
/** ≤ este número de dias dispara o status "warning" (âmbar). */
const WARNING_THRESHOLD_DAYS = 30

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/**
 * Avalia uma data de vencimento (ISO string) e retorna status + dias restantes.
 *
 * @param iso String ISO 8601 (com ou sem hora). `null`/`undefined`/inválida → `unknown`.
 * @param now Override para testes; default `new Date()`.
 */
export function getExpiryInfo(iso: string | null | undefined, now: Date = new Date()): ExpiryInfo {
  if (!iso) return { status: 'unknown', daysLeft: null }
  const expiry = new Date(iso)
  if (Number.isNaN(expiry.getTime())) return { status: 'unknown', daysLeft: null }

  const todayMs = startOfDay(now).getTime()
  const expiryMs = startOfDay(expiry).getTime()
  const daysLeft = Math.round((expiryMs - todayMs) / 86_400_000)

  let status: ExpiryStatus
  if (daysLeft < 0) status = 'expired'
  else if (daysLeft <= CRITICAL_THRESHOLD_DAYS) status = 'critical'
  else if (daysLeft <= WARNING_THRESHOLD_DAYS) status = 'warning'
  else status = 'ok'

  return { status, daysLeft }
}

/**
 * Texto humanizado para mostrar ao lado de uma data de vencimento.
 * Ex.: "venceu há 3 dias", "vence hoje", "vence em 18 dias".
 */
export function formatExpiryDelta(daysLeft: number | null): string | null {
  if (daysLeft === null) return null
  if (daysLeft < 0) {
    const abs = Math.abs(daysLeft)
    return abs === 1 ? 'venceu ontem' : `venceu há ${abs} dias`
  }
  if (daysLeft === 0) return 'vence hoje'
  if (daysLeft === 1) return 'vence amanhã'
  return `vence em ${daysLeft} dias`
}

/** Conveniência: só passa o ISO e recebe o texto, ignorando dados ausentes. */
export function describeExpiry(iso: string | null | undefined, now?: Date): string | null {
  return formatExpiryDelta(getExpiryInfo(iso, now).daysLeft)
}

/** True quando o status merece destaque visual (vencido, crítico ou em atenção). */
export function isExpiryActionable(status: ExpiryStatus): boolean {
  return status === 'expired' || status === 'critical' || status === 'warning'
}
